import { useState, useEffect } from 'react'
import { useAppDispatch, useIsAuthenticated, useUser } from '@/store/hooks'
import { loginUser, clearError } from '@/store/slices/authSlice'
import { useNavigate, Link } from 'react-router-dom'
import { sessionService } from '@/services/sessionService'
import { useAuthStore } from '@/store/authStore'

const DepartmentLoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isAuthenticated = useIsAuthenticated()
  const user = useUser()
  const { login: loginZustand } = useAuthStore()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && (user.role === 'department_head' || user.role === 'front_worker')) {
      navigate('/app/dashboard', { replace: true })
    }
  }, [isAuthenticated, user, navigate])

  // Clear any existing errors on component mount
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      console.log('Starting department login...')
      const resultAction = await dispatch(loginUser({
        email,
        password,
        loginType: 'staff' // Use 'staff' login type for department users
      }))

      if (loginUser.fulfilled.match(resultAction)) {
        const { user, sessionId, token, refreshToken } = resultAction.payload
        console.log('Login successful, user:', user)

        // Check if user has department privileges
        if (user && (user.role === 'department_head' || user.role === 'front_worker')) {
          console.log('Department user authenticated, starting session and navigating...')

          // Sync Zustand store with Redux state
          try {
            await loginZustand(email, password, 'staff')
          } catch (err) {
            // If Zustand login fails, manually set the state
            useAuthStore.setState({
              user,
              token,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }

          // Start session tracking
          if (sessionId) {
            sessionService.startSession(sessionId)
          }

          // Navigate to dashboard with replace to prevent going back to login
          navigate('/app/dashboard', { replace: true })
        } else {
          console.error('User role not authorized for department access:', user?.role)
          setError('Access denied. This login is for department staff only.')
        }
      } else {
        // Handle login failure
        const errorMessage = resultAction.payload as string
        console.error('Department login failed:', errorMessage)
        setError(errorMessage || 'Login failed. Please check your credentials.')
      }
    } catch (error: any) {
      console.error('Department login error:', error)
      setError(error.message || 'Login failed. Please check your credentials.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Department Login
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to access department management features
            </p>
          </div>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Department email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-indigo-600 hover:text-indigo-500">
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign in to Department'}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Not a department user?{' '}
              <Link to="/user-login" className="font-medium text-indigo-600 hover:text-indigo-500">
                User Login
              </Link>
            </p>
          </div>

          <div className="text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
              ← Back to Home
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DepartmentLoginPage
